import {
  DocumentQueryStatus,
  DocumentQueryUnavailableReason,
  KnowledgeBaseFeedbackPayload,
  KnowledgeBaseQueryResponse,
  RawDocumentQueryStatus,
  RawKnowledgeBaseQueryResponse
} from '@/interfaces/library/knowledgeBase'
import http from '@/http'

const transform = (rawStatus: RawDocumentQueryStatus): DocumentQueryStatus => {
  switch (rawStatus.type) {
    case 'available':
      return { type: 'available', message: rawStatus.message, reason: null }
    case 'no_content':
      return { type: 'no_content', message: rawStatus.message, reason: null }
    case 'not_ready':
      return { type: 'not_ready', message: rawStatus.message, reason: null }
    case 'service_error':
      return { type: 'service_error', message: rawStatus.message, reason: null }
    case 'token_limit_exceeded':
      return { type: 'token_limit_exceeded', message: rawStatus.message, reason: null }
    case 'unavailable':
      if (rawStatus.reason && Object.values(DocumentQueryUnavailableReason).includes(rawStatus.reason)) {
        return {
          type: 'unavailable',
          reason: rawStatus.reason,
          message: rawStatus.message
        }
      }
      throw new Error(`Invalid or missing reason for Unavailable status: ${rawStatus.reason}`)
    default:
      const exhaustiveCheck: never = rawStatus.type
      throw new Error(`Unhandled status type: ${exhaustiveCheck}`)
  }
}

const transformQueryResponse = (rawResponse: RawKnowledgeBaseQueryResponse): KnowledgeBaseQueryResponse => {
  switch (rawResponse.type) {
    case 'general':
      if (typeof rawResponse.responseText === 'string' && typeof rawResponse.requestId === 'string') {
        return { type: 'general', responseText: rawResponse.responseText, requestId: rawResponse.requestId }
      }
      throw new Error('Invalid or missing fields for general type')
    case 'rag':
      if (
        Array.isArray(rawResponse.citations) &&
        typeof rawResponse.formattedText === 'string' &&
        typeof rawResponse.hasMoreTokens === 'boolean' &&
        typeof rawResponse.requestId === 'string'
      ) {
        return {
          type: 'rag',
          citations: rawResponse.citations,
          formattedText: rawResponse.formattedText,
          hasMoreTokens: rawResponse.hasMoreTokens,
          requestId: rawResponse.requestId
        }
      }
      throw new Error('Invalid or missing fields for rag type')
    case 'summary':
      if (typeof rawResponse.documentSummary === 'string' && typeof rawResponse.requestId === 'string') {
        return { type: 'summary', documentSummary: rawResponse.documentSummary, requestId: rawResponse.requestId }
      }
      throw new Error('Invalid or missing fields for summary type')
    case 'translation':
      if (typeof rawResponse.responseText === 'string' && typeof rawResponse.requestId === 'string') {
        return {
          type: 'translation',
          responseText: rawResponse.responseText,
          requestId: rawResponse.requestId
        }
      }
      throw new Error('Invalid or missing fields for translation type')
    case 'error':
      if (typeof rawResponse.message === 'string') {
        return { type: 'error', message: rawResponse.message }
      }
      throw new Error('Invalid or missing message for error type')
    default:
      const exhaustiveCheck: never = rawResponse.type
      throw new Error(`Unhandled query response type: ${exhaustiveCheck}`)
  }
}

const getStatus = async (contentType: 'page' | 'media', contentId: string): Promise<DocumentQueryStatus> => {
  const url = contentType === 'page'
    ? `/knowledge-base/pages/${contentId}/status`
    : `/knowledge-base/media/${contentId}/status`

  const response = await http.get<RawDocumentQueryStatus>(url)
  return transform(response.data)
}

const postQuery = async (contentType: 'page' | 'media', contentId: string, query: string): Promise<KnowledgeBaseQueryResponse> => {
  const url = contentType === 'page'
    ? `/knowledge-base/pages/${contentId}/query`
    : `/knowledge-base/media/${contentId}/query`

  const response = await http.post<RawKnowledgeBaseQueryResponse>(url, { query })
  return transformQueryResponse(response.data)
}

const sendFeedback = async (requestId: string, payload: KnowledgeBaseFeedbackPayload): Promise<void> => {
  await http.put(`/knowledge-base/feedback/${requestId}`, payload)
}

export const knowledgeBase = {
  getStatus,
  postQuery,
  sendFeedback
}
